import type { FC } from 'react';
import React from 'react';
import { styled } from '@compiled/react';

import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { shouldDisplayExtensionAsInline } from '@confluence/fabric-extension-lib/entry-points/shouldDisplayExtensionAsInline.ts';

import {
	isDrawioExtensionKey,
	useConnectExtensionViewportSize,
} from '../useConnectExtensionViewportSize.ts';
import { useMacroHeightPreset } from '../useMacroHeightPreset.ts';

type ExtensionSkeletonProps = {
	height?: number | 'auto';
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineSkeleton = styled.span({
	boxSizing: 'border-box',
	display: 'inline-block',
	backgroundColor: token('color.skeleton'),
	borderRadius: token('radius.medium'),
	minWidth: '50px',
	height: '26px',
	padding: token('space.050'),
	verticalAlign: 'middle',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExtensionSkeleton = styled.div<ExtensionSkeletonProps>({
	display: 'block',
	backgroundColor: token('color.skeleton'),
	borderRadius: token('radius.medium'),
	width: '100%',
	minWidth: '60px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ height }) => (height === 'auto' ? height : height ? `${height}px` : '35px'),
	padding: token('space.050'),
	boxSizing: 'border-box',
});

export const LegacyMacroRendererShadow: FC<{
	showSpinner?: boolean;
	node: any;
	isRenderer?: boolean;
}> = ({ showSpinner, node, isRenderer = true }) => {
	const { getPresetMinHeight, getPresetHeight } = useMacroHeightPreset(isRenderer);
	const { viewportHeight } = useConnectExtensionViewportSize(node);
	const displayAsInline = shouldDisplayExtensionAsInline(node);

	if (node && (node.type === 'inlineExtension' || displayAsInline)) {
		return (
			<InlineSkeleton data-testid="ext-skeleton">
				{showSpinner && <Spinner size="small" interactionName="legacy-macro-inline-skeleton" />}
			</InlineSkeleton>
		);
	}

	return (
		<ExtensionSkeleton
			data-testid="ext-skeleton"
			height={
				// The draw.io experiment is checked first so draw.io presets its height even when the Connect preset gate is off
				getPresetMinHeight?.(node?.extensionKey) !== undefined ||
				getPresetHeight?.(node?.extensionKey) !== undefined
					? 'auto'
					: (isDrawioExtensionKey(node?.extensionKey) &&
								expVal('confluence_preset_height_migrated_drawio', 'isEnabled', false)) ||
						  // oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
						  fg('confluence_connect_macro_preset_height')
						? viewportHeight
						: undefined
			}
		>
			{showSpinner && <Spinner size="small" interactionName="legacy-macro-extension-skeleton" />}
		</ExtensionSkeleton>
	);
};
